package com.sddamico.draftmachine.util

import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiGet
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.data.ExternalProvider
import com.sddamico.draftmachine.models.EspnLeaguePreview
import com.sddamico.draftmachine.models.FantraxLeagueOption
import com.sddamico.draftmachine.models.FantraxTeamOption
import com.sddamico.draftmachine.models.LeagueConnectionResponse
import com.sddamico.draftmachine.models.LeagueConnectionStateResponse
import com.sddamico.draftmachine.models.LookupFantraxLeaguesRequest
import com.sddamico.draftmachine.models.LookupFantraxTeamsRequest
import com.sddamico.draftmachine.models.LookupEspnLeagueRequest
import com.sddamico.draftmachine.models.OwnershipSyncResponse
import com.sddamico.draftmachine.models.SaveLeagueConnectionRequest
import com.sddamico.draftmachine.models.TriggerOwnershipSyncRequest
import kotlinx.serialization.builtins.ListSerializer

// UI-free helpers for the league connection screen, mirroring util/DraftActions.kt's
// sealed-result-per-action convention.

sealed interface LookupLeaguesResult {
  data class Success(val leagues: List<FantraxLeagueOption>) : LookupLeaguesResult
  data class Failure(val message: String) : LookupLeaguesResult
}

sealed interface LookupTeamsResult {
  data class Success(val teams: List<FantraxTeamOption>) : LookupTeamsResult
  data class Failure(val message: String) : LookupTeamsResult
}

sealed interface LookupEspnLeagueResult {
  data class Success(val league: EspnLeaguePreview) : LookupEspnLeagueResult
  data class Failure(val message: String) : LookupEspnLeagueResult
}

sealed interface SaveConnectionResult {
  data class Success(val connection: LeagueConnectionResponse) : SaveConnectionResult
  data class Failure(val message: String) : SaveConnectionResult
}

sealed interface TriggerSyncResult {
  data class Success(val sync: OwnershipSyncResponse) : TriggerSyncResult
  data class Failure(val message: String) : TriggerSyncResult
}

suspend fun fetchLeagueConnection(leagueId: String): LoadState<LeagueConnectionStateResponse> =
  try {
    LoadState.Ready(
      apiGet("/api/league-connection?leagueId=$leagueId", LeagueConnectionStateResponse.serializer())
    )
  } catch (e: ApiException) {
    LoadState.Failed(e.errorMessage)
  } catch (_: Throwable) {
    LoadState.Failed(UNREACHABLE_MESSAGE)
  }

suspend fun lookupFantraxLeagues(userSecretId: String): LookupLeaguesResult =
  try {
    LookupLeaguesResult.Success(
      apiPost(
        "/api/fantrax-leagues",
        apiJson.encodeToString(LookupFantraxLeaguesRequest.serializer(), LookupFantraxLeaguesRequest(userSecretId)),
        ListSerializer(FantraxLeagueOption.serializer()),
      )
    )
  } catch (e: ApiException) {
    LookupLeaguesResult.Failure(e.errorMessage)
  } catch (_: Throwable) {
    LookupLeaguesResult.Failure(UNREACHABLE_MESSAGE)
  }

/** The teams in a Fantrax league. Public roster read server-side, so no secret is involved. */
suspend fun lookupFantraxTeams(externalLeagueId: String): LookupTeamsResult =
  try {
    LookupTeamsResult.Success(
      apiPost(
        "/api/fantrax-teams",
        apiJson.encodeToString(
          LookupFantraxTeamsRequest.serializer(),
          LookupFantraxTeamsRequest(externalLeagueId),
        ),
        ListSerializer(FantraxTeamOption.serializer()),
      )
    )
  } catch (e: ApiException) {
    LookupTeamsResult.Failure(e.errorMessage)
  } catch (_: Throwable) {
    LookupTeamsResult.Failure(UNREACHABLE_MESSAGE)
  }

suspend fun lookupEspnLeague(
  externalLeagueId: String,
  sport: Sport,
  season: Int,
  espnS2: String,
  espnSwid: String,
): LookupEspnLeagueResult =
  try {
    LookupEspnLeagueResult.Success(
      apiPost(
        "/api/espn-league",
        apiJson.encodeToString(
          LookupEspnLeagueRequest.serializer(),
          LookupEspnLeagueRequest(externalLeagueId, sport, season, espnS2, espnSwid),
        ),
        EspnLeaguePreview.serializer(),
      )
    )
  } catch (e: ApiException) {
    LookupEspnLeagueResult.Failure(e.errorMessage)
  } catch (_: Throwable) {
    LookupEspnLeagueResult.Failure(UNREACHABLE_MESSAGE)
  }

suspend fun saveLeagueConnection(
  leagueId: String,
  provider: ExternalProvider,
  externalLeagueId: String,
  sport: Sport,
  externalSeason: Int? = null,
  userSecretId: String? = null,
  espnS2: String? = null,
  espnSwid: String? = null,
): SaveConnectionResult =
  try {
    SaveConnectionResult.Success(
      apiPost(
        "/api/league-connection",
        apiJson.encodeToString(
          SaveLeagueConnectionRequest.serializer(),
          SaveLeagueConnectionRequest(
            leagueId = leagueId,
            provider = provider,
            externalLeagueId = externalLeagueId,
            externalSeason = externalSeason,
            sport = sport,
            userSecretId = userSecretId,
            espnS2 = espnS2,
            espnSwid = espnSwid,
          ),
        ),
        LeagueConnectionResponse.serializer(),
      )
    )
  } catch (e: ApiException) {
    SaveConnectionResult.Failure(e.errorMessage)
  } catch (_: Throwable) {
    SaveConnectionResult.Failure(UNREACHABLE_MESSAGE)
  }

suspend fun triggerOwnershipSync(leagueId: String): TriggerSyncResult =
  try {
    TriggerSyncResult.Success(
      apiPost(
        "/api/league-ownership-sync",
        apiJson.encodeToString(TriggerOwnershipSyncRequest.serializer(), TriggerOwnershipSyncRequest(leagueId)),
        OwnershipSyncResponse.serializer(),
      )
    )
  } catch (e: ApiException) {
    TriggerSyncResult.Failure(e.errorMessage)
  } catch (_: Throwable) {
    TriggerSyncResult.Failure(UNREACHABLE_MESSAGE)
  }
